import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { db } from '../firebase/config';
import { doc, getDoc, setDoc, updateDoc, increment } from 'firebase/firestore';
import {
    Package, ShoppingCart, Heart, Star, Eye, TrendingUp,
    ChevronLeft, ChevronRight, X, Truck, RotateCcw, Shield,
    Store, ChevronDown, Check, AlertCircle, Info, MessageSquare, FileText
} from 'lucide-react';
import ProductReviewList from './ProductReviewList';

const ProductDetail = ({ userInfo, match }) => {
    const navigate = useNavigate();
    const [loading, setLoading] = useState(true);
    const [product, setProduct] = useState(null);
    const [seller, setSeller] = useState(null);
    const [currentImageIndex, setCurrentImageIndex] = useState(0);
    const [quantity, setQuantity] = useState(1);
    const [showImageModal, setShowImageModal] = useState(false);
    const [isLiked, setIsLiked] = useState(false);
    const [activeTab, setActiveTab] = useState('description');

    // 옵션 관련 상태
    const [selectedOptions, setSelectedOptions] = useState({});
    const [optionError, setOptionError] = useState('');

    const productId = match?.params?.id || window.location.pathname.split('/').pop();

    // 새로운 전문 카테고리
    const categories = {
        'implant': '임플란트',
        'restorative': '수복재료',
        'endodontic': '근관치료',
        'preventive': '예방치료',
        'impression': '인상재료',
        'orthodontic': '교정재료',
        'surgical': '외과용품',
        'hygiene': '위생용품',
        'equipment': '장비',
        'lab-material': '기공재료',
        'other': '기타',
        // 이전 카테고리 호환
        'crown': '크라운/브릿지',
        'denture': '의치',
        'ceramic': '세라믹',
        'metal': '금속재료',
        'consumable': '소모품'
    };

    const loadProduct = useCallback(async () => {
        try {
            setLoading(true);

            const docRef = doc(db, 'marketplaceProducts', productId);
            const docSnap = await getDoc(docRef);

            if (!docSnap.exists()) {
                alert('상품을 찾을 수 없습니다.');
                window.location.href = '/marketplace';
                return;
            }

            const productData = { id: docSnap.id, ...docSnap.data() };

            // 레거시 options → optionGroups 변환
            if ((!productData.optionGroups || productData.optionGroups.length === 0) && productData.options) {
                const legacyOptions = productData.options;
                if (Array.isArray(legacyOptions) && legacyOptions.length > 0) {
                    // 레거시 options가 [{name, values}] 형태인 경우 그대로 사용
                    if (legacyOptions[0]?.name && Array.isArray(legacyOptions[0]?.values)) {
                        productData.optionGroups = legacyOptions;
                    }
                    // 레거시 options가 문자열 배열인 경우 단일 옵션그룹으로 변환
                    else if (typeof legacyOptions[0] === 'string') {
                        productData.optionGroups = [{
                            name: '옵션',
                            values: legacyOptions.map(v => ({ value: v, additionalPrice: 0 }))
                        }];
                    }
                } else if (typeof legacyOptions === 'object' && !Array.isArray(legacyOptions)) {
                    // 레거시 options가 {key: [values]} 형태인 경우
                    productData.optionGroups = Object.entries(legacyOptions).map(([name, values]) => ({
                        name,
                        values: Array.isArray(values) ? values.map(v =>
                            typeof v === 'string' ? { value: v, additionalPrice: 0 } : v
                        ) : []
                    }));
                }
            }

            setProduct(productData);

            // 옵션 초기화 (optionGroups에 올바른 {name, values} 구조가 저장됨)
            const optGroups = productData.optionGroups || [];
            if (Array.isArray(optGroups) && optGroups.length > 0) {
                const initialOptions = {};
                optGroups.forEach(opt => {
                    if (opt && opt.name && Array.isArray(opt.values) && opt.values.length > 0) {
                        initialOptions[opt.name] = null;
                    }
                });
                setSelectedOptions(initialOptions);
            }

            // 조회수 증가
            try { await updateDoc(docRef, { viewCount: increment(1) }); } catch (e) {}

            // 판매자 정보
            if (productData.sellerId) {
                setSeller({
                    name: productData.sellerName,
                    type: productData.sellerType,
                    companyId: productData.companyId,
                    companyName: productData.companyName
                });
            }

        } catch (error) {
            console.error('상품 로딩 오류:', error);
            alert('상품 정보를 불러오는데 실패했습니다.');
        } finally {
            setLoading(false);
        }
    }, [productId]);

    useEffect(() => {
        loadProduct();
    }, [loadProduct]);

    // 옵션 선택 핸들러
    const handleOptionSelect = (optionName, value) => {
        setSelectedOptions(prev => ({
            ...prev,
            [optionName]: value
        }));
        setOptionError('');
    };

    // 선택된 옵션의 추가 금액 계산
    const getOptionsAdditionalPrice = () => {
        const optGroups = product?.optionGroups;
        if (!optGroups || !Array.isArray(optGroups) || optGroups.length === 0) return 0;

        let additionalPrice = 0;
        optGroups.forEach(option => {
            if (!option || !option.name || !Array.isArray(option.values)) return;

            const selectedValue = selectedOptions[option.name];
            if (selectedValue) {
                const valueInfo = option.values.find(v => (v?.value || v) === selectedValue);
                if (valueInfo) {
                    additionalPrice += valueInfo?.additionalPrice || 0;
                }
            }
        });
        return additionalPrice;
    };

    // 총 가격 계산
    const getTotalPrice = () => {
        const basePrice = product?.price || 0;
        const optionPrice = getOptionsAdditionalPrice();
        return (basePrice + optionPrice) * quantity;
    };

    // 단가 계산 (옵션 포함)
    const getUnitPrice = () => {
        return (product?.price || 0) + getOptionsAdditionalPrice();
    };

    // 옵션 선택 검증
    const validateOptions = () => {
        const optGroups = product?.optionGroups;
        if (!optGroups || !Array.isArray(optGroups) || optGroups.length === 0) return true;

        // 유효한 옵션만 필터링
        const validOptions = optGroups.filter(opt =>
            opt && opt.name && Array.isArray(opt.values) && opt.values.length > 0
        );

        for (const option of validOptions) {
            if (!selectedOptions[option.name]) {
                setOptionError(`"${option.name}" 옵션을 선택해주세요.`);
                return false;
            }
        }
        return true;
    };

    // 선택된 옵션 텍스트 생성
    const getSelectedOptionsText = () => {
        const optGroups = product?.optionGroups;
        if (!optGroups || !Array.isArray(optGroups) || optGroups.length === 0) return '';

        const parts = [];
        optGroups.forEach(option => {
            if (!option || !option.name) return;
            const selected = selectedOptions[option.name];
            if (selected) {
                parts.push(`${option.name}: ${selected}`);
            }
        });
        return parts.join(' / ');
    };

    // 유효한 옵션이 있는지 체크
    const hasValidOptions = () => {
        const optGroups = product?.optionGroups;
        if (!optGroups || !Array.isArray(optGroups)) return false;
        return optGroups.some(opt =>
            opt && opt.name && Array.isArray(opt.values) && opt.values.length > 0
        );
    };

    const handleAddToCart = async () => {
        if (!userInfo) {
            alert('로그인이 필요합니다.');
            navigate('/login');
            return;
        }

        if (!validateOptions()) return;

        const moq = product.minOrder || product.moq || 1;
        if (quantity < moq) {
            alert(`최소 주문 수량은 ${moq}개입니다.`);
            return;
        }

        if (quantity > product.stock) {
            alert('재고가 부족합니다.');
            return;
        }

        try {
            const cartItem = {
                id: product.id,
                name: product.name,
                price: getUnitPrice(),
                basePrice: product.price,
                quantity: quantity,
                image: product.images?.[0] || product.imageUrl || null,
                imageUrl: product.images?.[0] || product.imageUrl || null,
                stock: product.stock,
                unit: product.unit || 'EA',
                brand: product.brand || '',
                sellerId: product.sellerId || null,
                sellerName: product.sellerName || '알 수 없음',
                sellerType: product.sellerType || 'unknown',
                selectedOptions: { ...selectedOptions },
                optionsText: getSelectedOptionsText(),
                additionalPrice: getOptionsAdditionalPrice()
            };

            const cartUserId = userInfo?.uid;
            console.log('🛒 장바구니 추가 - cartUserId:', cartUserId, 'userInfo:', userInfo?.uid, userInfo?.companyId);
            const cartRef = doc(db, 'userCarts', cartUserId);
            const cartSnap = await getDoc(cartRef);
            const currentCart = cartSnap.exists() ? (cartSnap.data().items || []) : [];

            // 같은 상품 + 같은 옵션인지 확인
            const optionsString = JSON.stringify(selectedOptions);
            const existingIndex = currentCart.findIndex(item =>
                (item.productId || item.id) === product.id &&
                JSON.stringify(item.selectedOptions) === optionsString
            );

            if (existingIndex > -1) {
                currentCart[existingIndex].quantity += quantity;
                alert(`장바구니에 ${quantity}개가 추가되었습니다! (총 ${currentCart[existingIndex].quantity}개)`);
            } else {
                // Firestore userCarts 형식으로 저장
                currentCart.push({
                    ...cartItem,
                    productId: cartItem.id,
                    productName: cartItem.name,
                    addedAt: new Date()
                });
                alert(`${product.name} ${quantity}개를 장바구니에 담았습니다!`);
            }

            await setDoc(cartRef, { items: currentCart, updatedAt: new Date() });

            if (window.confirm('장바구니로 이동하시겠습니까?')) {
                navigate('/cart');
            }
        } catch (error) {
            console.error('장바구니 추가 오류:', error);
            alert('장바구니에 추가하는 중 오류가 발생했습니다.');
        }
    };

    const handleBuyNow = () => {
        if (!userInfo) {
            alert('로그인이 필요합니다.');
            navigate('/login');
            return;
        }

        if (!validateOptions()) return;

        const moq = product.minOrder || product.moq || 1;
        if (quantity < moq) {
            alert(`최소 주문 수량은 ${moq}개입니다.`);
            return;
        }

        if (quantity > product.stock) {
            alert('재고가 부족합니다.');
            return;
        }

        navigate('/checkout', {
            state: {
                items: [{
                    id: product.id,
                    name: product.name,
                    price: getUnitPrice(),
                    basePrice: product.price,
                    quantity: quantity,
                    image: product.images?.[0] || product.imageUrl || null,
                    stock: product.stock,
                    unit: product.unit || 'EA',
                    sellerId: product.sellerId || null,
                    sellerName: product.sellerName || '알 수 없음',
                    sellerType: product.sellerType || 'unknown',
                    selectedOptions: { ...selectedOptions },
                    optionsText: getSelectedOptionsText(),
                    additionalPrice: getOptionsAdditionalPrice()
                }],
                buyNow: true
            }
        });
    };

    const handleLike = async () => {
        if (!userInfo) {
            alert('로그인이 필요합니다.');
            return;
        }

        try {
            const newLikedState = !isLiked;
            setIsLiked(newLikedState);

            await updateDoc(doc(db, 'marketplaceProducts', productId), {
                likeCount: increment(newLikedState ? 1 : -1)
            });

            setProduct(prev => ({
                ...prev,
                likeCount: (prev.likeCount || 0) + (newLikedState ? 1 : -1)
            }));
        } catch (error) {
            console.error('좋아요 오류:', error);
        }
    };

    const formatPrice = (price) => {
        return new Intl.NumberFormat('ko-KR').format(price);
    };

    const getCategoryLabel = (value) => {
        return categories[value] || value;
    };

    const nextImage = () => {
        if (product.images && product.images.length > 0) {
            setCurrentImageIndex((prev) =>
                prev === product.images.length - 1 ? 0 : prev + 1
            );
        }
    };

    const prevImage = () => {
        if (product.images && product.images.length > 0) {
            setCurrentImageIndex((prev) =>
                prev === 0 ? product.images.length - 1 : prev - 1
            );
        }
    };

    // 할인율 계산
    const getDiscountRate = () => {
        if (!product?.originalPrice || product.originalPrice <= product.price) return 0;
        return Math.round((1 - product.price / product.originalPrice) * 100);
    };

    if (loading) {
        return (
            <div style={styles.loadingContainer}>
                <div style={styles.spinner}></div>
                <div style={styles.loadingText}>상품 정보를 불러오는 중...</div>
            </div>
        );
    }

    if (!product) {
        return null;
    }

    const discountRate = getDiscountRate();

    return (
        <div style={styles.container}>
            {/* 뒤로가기 + 브레드크럼 */}
            <div style={styles.breadcrumb}>
                <button
                    style={styles.backButton}
                    onClick={() => window.history.back()}
                >
                    <ChevronLeft size={20} />
                </button>
                <span style={styles.breadcrumbText}>
                    마켓플레이스 &gt; {getCategoryLabel(product.category)}
                    {product.subCategory && ` > ${product.subCategory}`}
                </span>
            </div>

            <div style={styles.productContainer}>
                {/* 이미지 갤러리 */}
                <div style={styles.imageSection}>
                    <div style={styles.mainImageContainer}>
                        {product.images && product.images.length > 0 ? (
                            <>
                                <img
                                    src={product.images[currentImageIndex]}
                                    alt={product.name}
                                    style={styles.mainImage}
                                    onClick={() => setShowImageModal(true)}
                                />
                                {product.images.length > 1 && (
                                    <>
                                        <button
                                            style={{...styles.imageNav, left: '16px'}}
                                            onClick={prevImage}
                                        >
                                            <ChevronLeft size={24} />
                                        </button>
                                        <button
                                            style={{...styles.imageNav, right: '16px'}}
                                            onClick={nextImage}
                                        >
                                            <ChevronRight size={24} />
                                        </button>
                                    </>
                                )}
                                <div style={styles.imageCounter}>
                                    {currentImageIndex + 1} / {product.images.length}
                                </div>
                            </>
                        ) : (
                            <div style={styles.noImage}>
                                <Package size={80} color="#cbd5e1" />
                            </div>
                        )}

                        {/* 할인 뱃지 */}
                        {discountRate > 0 && (
                            <div style={styles.discountBadge}>{discountRate}%</div>
                        )}
                    </div>

                    {/* 썸네일 */}
                    {product.images && product.images.length > 1 && (
                        <div style={styles.thumbnailGrid}>
                            {product.images.map((img, index) => (
                                <div
                                    key={index}
                                    style={{
                                        ...styles.thumbnail,
                                        border: index === currentImageIndex ? '3px solid #6366f1' : '2px solid #e2e8f0'
                                    }}
                                    onClick={() => setCurrentImageIndex(index)}
                                >
                                    <img src={img} alt={`${product.name} ${index + 1}`} style={styles.thumbnailImage} />
                                </div>
                            ))}
                        </div>
                    )}
                </div>

                {/* 상품 정보 */}
                <div style={styles.infoSection}>
                    {/* 브랜드 & 카테고리 */}
                    <div style={styles.topBadges}>
                        <span style={styles.categoryBadge}>
                            {getCategoryLabel(product.category)}
                        </span>
                        {product.brand && (
                            <span style={styles.brandBadge}>{product.brand}</span>
                        )}
                    </div>

                    <h1 style={styles.productTitle}>{product.name}</h1>

                    {/* 통계 */}
                    <div style={styles.statsRow}>
                        <div style={styles.stat}>
                            <Eye size={16} />
                            <span>조회 {product.viewCount || 0}</span>
                        </div>
                        <div style={styles.stat}>
                            <Heart size={16} />
                            <span>찜 {product.likeCount || 0}</span>
                        </div>
                        <div style={styles.stat}>
                            <TrendingUp size={16} />
                            <span>판매 {product.orderCount || 0}건</span>
                        </div>
                        {product.rating > 0 && (
                            <div style={styles.stat}>
                                <Star size={16} color="#f59e0b" fill="#f59e0b" />
                                <span>{product.rating.toFixed(1)} ({product.reviewCount})</span>
                            </div>
                        )}
                    </div>

                    {/* 가격 섹션 (네이버 스타일) */}
                    <div style={styles.priceSection}>
                        {discountRate > 0 && (
                            <div style={styles.originalPriceRow}>
                                <span style={styles.originalPriceLabel}>정가</span>
                                <span style={styles.originalPrice}>{formatPrice(product.originalPrice)}원</span>
                            </div>
                        )}
                        <div style={styles.salePriceRow}>
                            {discountRate > 0 && (
                                <span style={styles.discountRate}>{discountRate}%</span>
                            )}
                            <span style={styles.salePrice}>{formatPrice(product.price)}원</span>
                            <span style={styles.unit}>/ {product.unit || 'EA'}</span>
                        </div>
                    </div>

                    {/* 배송/반품 정보 */}
                    <div style={styles.deliveryInfo}>
                        <div style={styles.deliveryItem}>
                            <Truck size={18} color="#64748b" />
                            <span>{product.shippingInfo || '무료배송'}</span>
                        </div>
                        <div style={styles.deliveryItem}>
                            <RotateCcw size={18} color="#64748b" />
                            <span>{product.returnPolicy || '7일 이내 반품 가능'}</span>
                        </div>
                        {product.certifications && (
                            <div style={styles.deliveryItem}>
                                <Shield size={18} color="#64748b" />
                                <span>{product.certifications}</span>
                            </div>
                        )}
                    </div>

                    {/* 세금계산서 발행 가능 */}
                    {product.taxInvoice && (
                        <div style={{display:'flex',alignItems:'center',gap:'6px',padding:'6px 12px',backgroundColor:'#eef2ff',borderRadius:'8px',fontSize:'13px',color:'#6366f1',fontWeight:'600',marginTop:'8px'}}>
                            <FileText size={14} />
                            세금계산서 발행 가능
                        </div>
                    )}

                    {/* 옵션 선택 (네이버 스타일) */}
                    {product.optionGroups && Array.isArray(product.optionGroups) && product.optionGroups.length > 0 &&
                     product.optionGroups.some(opt => opt && opt.name && Array.isArray(opt.values) && opt.values.length > 0) && (
                        <div style={styles.optionsSection}>
                            <div style={styles.optionsTitle}>
                                <Info size={16} />
                                옵션 선택
                            </div>

                            {product.optionGroups
                                .filter(option => option && option.name && Array.isArray(option.values) && option.values.length > 0)
                                .map((option, idx) => (
                                <div key={idx} style={styles.optionGroup}>
                                    <label style={styles.optionLabel}>
                                        {option.name}
                                        <span style={styles.requiredMark}>*</span>
                                    </label>
                                    <div style={styles.optionValues}>
                                        {option.values.map((val, vIdx) => {
                                            const isSelected = selectedOptions[option.name] === (val?.value || val);
                                            const displayValue = val?.value || val;
                                            const addPrice = val?.additionalPrice || 0;
                                            return (
                                                <button
                                                    key={vIdx}
                                                    style={{
                                                        ...styles.optionButton,
                                                        ...(isSelected ? styles.optionButtonSelected : {})
                                                    }}
                                                    onClick={() => handleOptionSelect(option.name, displayValue)}
                                                >
                                                    <span>{displayValue}</span>
                                                    {addPrice > 0 && (
                                                        <span style={styles.optionAddPrice}>
                                                            +{formatPrice(addPrice)}원
                                                        </span>
                                                    )}
                                                    {isSelected && <Check size={16} style={styles.checkIcon} />}
                                                </button>
                                            );
                                        })}
                                    </div>
                                </div>
                            ))}

                            {optionError && (
                                <div style={styles.optionError}>
                                    <AlertCircle size={16} />
                                    {optionError}
                                </div>
                            )}
                        </div>
                    )}

                    {/* 수량 선택 */}
                    <div style={styles.quantitySection}>
                        <div style={styles.quantityLeft}>
                            <span style={styles.quantityLabel}>수량</span>
                            {product.minOrder > 1 && (
                                <span style={styles.minOrderBadge}>
                                    최소 {product.minOrder}개
                                </span>
                            )}
                        </div>
                        <div style={styles.quantityControl}>
                            <button
                                style={styles.quantityButton}
                                onClick={() => setQuantity(Math.max(product.minOrder || 1, quantity - 1))}
                                disabled={quantity <= (product.minOrder || 1)}
                            >
                                -
                            </button>
                            <input
                                type="number"
                                value={quantity}
                                onChange={(e) => setQuantity(Math.max(product.minOrder || 1, parseInt(e.target.value) || 1))}
                                style={styles.quantityInput}
                                min={product.minOrder || 1}
                                max={product.stock}
                            />
                            <button
                                style={styles.quantityButton}
                                onClick={() => setQuantity(Math.min(product.stock, quantity + 1))}
                                disabled={quantity >= product.stock}
                            >
                                +
                            </button>
                        </div>
                    </div>

                    {/* MOQ 경고 */}
                    {(product.minOrder || product.moq) && quantity < (product.minOrder || product.moq) && (
                        <div style={{display:'flex',alignItems:'center',gap:'6px',padding:'8px 12px',backgroundColor:'#fef3c7',borderRadius:'8px',fontSize:'13px',color:'#92400e',fontWeight:'500',marginTop:'8px'}}>
                            <AlertCircle size={16} color="#f59e0b" />
                            최소 주문 수량은 {product.minOrder || product.moq}개입니다.
                        </div>
                    )}

                    {/* 재고 */}
                    <div style={styles.stockInfo}>
                        <Package size={18} />
                        <span style={styles.stockLabel}>재고:</span>
                        <span style={{
                            ...styles.stockValue,
                            color: product.stock > 10 ? '#059669' : product.stock > 0 ? '#f59e0b' : '#dc2626'
                        }}>
                            {product.stock > 0 ? `${product.stock}개 남음` : '품절'}
                        </span>
                    </div>

                    {/* 선택한 상품 정보 (네이버 스타일) */}
                    {(hasValidOptions() && Object.values(selectedOptions).some(v => v)) && (
                        <div style={styles.selectedProductBox}>
                            <div style={styles.selectedProductInfo}>
                                <span style={styles.selectedProductName}>
                                    {product.name}
                                </span>
                                <span style={styles.selectedOptionsText}>
                                    {getSelectedOptionsText()}
                                </span>
                            </div>
                            <div style={styles.selectedProductPrice}>
                                {formatPrice(getUnitPrice())}원 x {quantity}개
                            </div>
                        </div>
                    )}

                    {/* 총 금액 */}
                    <div style={styles.totalSection}>
                        <span style={styles.totalLabel}>총 상품금액</span>
                        <div style={styles.totalRight}>
                            <span style={styles.totalQuantity}>({quantity}개)</span>
                            <span style={styles.totalPrice}>
                                {formatPrice(getTotalPrice())}원
                            </span>
                        </div>
                    </div>

                    {/* 구매 버튼 */}
                    <div style={styles.actionButtons}>
                        <button
                            style={styles.likeButton}
                            onClick={handleLike}
                        >
                            <Heart
                                size={22}
                                color={isLiked ? '#ef4444' : '#64748b'}
                                fill={isLiked ? '#ef4444' : 'none'}
                            />
                        </button>
                        <button
                            style={styles.cartButton}
                            onClick={handleAddToCart}
                            disabled={product.stock === 0}
                        >
                            <ShoppingCart size={20} />
                            장바구니
                        </button>
                        <button
                            style={styles.buyButton}
                            onClick={handleBuyNow}
                            disabled={product.stock === 0}
                        >
                            바로구매
                        </button>
                    </div>

                    {/* 판매자 문의 버튼 */}
                    {seller && product.sellerId && (
                        <button
                            style={styles.inquiryButton}
                            onClick={() => {
                                if (!userInfo) {
                                    alert('로그인이 필요합니다.');
                                    navigate('/login');
                                    return;
                                }
                                navigate('/chat', {
                                    state: {
                                        type: 'product_inquiry',
                                        targetUserId: product.sellerId,
                                        productId: product.id,
                                        productName: product.name,
                                        productImage: product.images?.[0] || null
                                    }
                                });
                            }}
                        >
                            <MessageSquare size={18} />
                            판매자에게 문의
                        </button>
                    )}

                    {/* 판매자 정보 */}
                    {seller && (
                        <div style={styles.sellerInfo}>
                            <Store size={18} color="#64748b" />
                            <div style={styles.sellerDetails}>
                                <div style={styles.sellerLabel}>판매자</div>
                                <div style={styles.sellerName}>
                                    {seller.companyName || seller.name}
                                </div>
                                {seller.companyName && seller.name && seller.companyName !== seller.name && (
                                    <div style={styles.sellerSubName}>{seller.name}</div>
                                )}
                            </div>
                            <button
                                style={styles.sellerButton}
                                onClick={() => navigate(`/marketplace/seller/${product.sellerId}`)}
                            >
                                스토어 방문
                            </button>
                        </div>
                    )}
                </div>
            </div>

            {/* 상세 정보 탭 */}
            <div style={styles.detailSection}>
                <div style={styles.detailTabs}>
                    <button
                        style={{
                            ...styles.tab,
                            ...(activeTab === 'description' ? styles.activeTab : {})
                        }}
                        onClick={() => setActiveTab('description')}
                    >
                        상품설명
                    </button>
                    <button
                        style={{
                            ...styles.tab,
                            ...(activeTab === 'specs' ? styles.activeTab : {})
                        }}
                        onClick={() => setActiveTab('specs')}
                    >
                        상세정보
                    </button>
                    <button
                        style={{
                            ...styles.tab,
                            ...(activeTab === 'shipping' ? styles.activeTab : {})
                        }}
                        onClick={() => setActiveTab('shipping')}
                    >
                        배송/반품
                    </button>
                    <button
                        style={{
                            ...styles.tab,
                            ...(activeTab === 'reviews' ? styles.activeTab : {})
                        }}
                        onClick={() => setActiveTab('reviews')}
                    >
                        리뷰 ({product.reviewCount || 0})
                    </button>
                </div>

                <div style={styles.detailContent}>
                    {activeTab === 'description' && (
                        <>
                            {product.description && (
                                <div style={styles.descriptionSection}>
                                    <h2 style={styles.detailTitle}>상품 설명</h2>
                                    <p style={styles.descriptionText}>{product.description}</p>
                                </div>
                            )}

                            {product.features && (
                                <div style={styles.descriptionSection}>
                                    <h2 style={styles.detailTitle}>주요 특징</h2>
                                    <p style={styles.descriptionText}>{product.features}</p>
                                </div>
                            )}
                        </>
                    )}

                    {activeTab === 'specs' && (
                        <div style={styles.specsTable}>
                            <table style={styles.infoTable}>
                                <tbody>
                                    {product.brand && (
                                        <tr>
                                            <th style={styles.infoTh}>브랜드</th>
                                            <td style={styles.infoTd}>{product.brand}</td>
                                        </tr>
                                    )}
                                    {product.manufacturer && (
                                        <tr>
                                            <th style={styles.infoTh}>제조사</th>
                                            <td style={styles.infoTd}>{product.manufacturer}</td>
                                        </tr>
                                    )}
                                    {product.origin && (
                                        <tr>
                                            <th style={styles.infoTh}>원산지</th>
                                            <td style={styles.infoTd}>{product.origin}</td>
                                        </tr>
                                    )}
                                    {product.specifications && (
                                        <tr>
                                            <th style={styles.infoTh}>제품사양</th>
                                            <td style={styles.infoTd}>{product.specifications}</td>
                                        </tr>
                                    )}
                                    {product.certifications && (
                                        <tr>
                                            <th style={styles.infoTh}>인증정보</th>
                                            <td style={styles.infoTd}>{product.certifications}</td>
                                        </tr>
                                    )}
                                    <tr>
                                        <th style={styles.infoTh}>단위</th>
                                        <td style={styles.infoTd}>{product.unit || 'EA'}</td>
                                    </tr>
                                    {product.minOrder > 1 && (
                                        <tr>
                                            <th style={styles.infoTh}>최소주문</th>
                                            <td style={styles.infoTd}>{product.minOrder}개</td>
                                        </tr>
                                    )}
                                </tbody>
                            </table>
                        </div>
                    )}

                    {activeTab === 'shipping' && (
                        <div style={styles.shippingInfo}>
                            <div style={styles.shippingCard}>
                                <h3 style={styles.shippingTitle}>
                                    <Truck size={20} />
                                    배송 정보
                                </h3>
                                <p style={styles.shippingText}>
                                    {product.shippingInfo || '무료배송 (제주/도서산간 추가비용 발생)'}
                                </p>
                            </div>

                            <div style={styles.shippingCard}>
                                <h3 style={styles.shippingTitle}>
                                    <RotateCcw size={20} />
                                    반품/교환 정책
                                </h3>
                                <p style={styles.shippingText}>
                                    {product.returnPolicy || '상품 수령 후 7일 이내 반품/교환 가능\n개봉 후 상품은 반품이 불가할 수 있습니다.'}
                                </p>
                            </div>
                        </div>
                    )}

                    {activeTab === 'reviews' && (
                        <ProductReviewList
                            productId={productId}
                            productName={product.name}
                        />
                    )}
                </div>
            </div>

            {/* 이미지 모달 */}
            {showImageModal && product.images && (
                <div style={styles.modalOverlay} onClick={() => setShowImageModal(false)}>
                    <div style={styles.imageModal} onClick={(e) => e.stopPropagation()}>
                        <button
                            style={styles.modalClose}
                            onClick={() => setShowImageModal(false)}
                        >
                            <X size={24} />
                        </button>
                        <img
                            src={product.images[currentImageIndex]}
                            alt={product.name}
                            style={styles.modalImage}
                        />
                        {product.images.length > 1 && (
                            <>
                                <button
                                    style={{...styles.modalNav, left: '24px'}}
                                    onClick={prevImage}
                                >
                                    <ChevronLeft size={32} />
                                </button>
                                <button
                                    style={{...styles.modalNav, right: '24px'}}
                                    onClick={nextImage}
                                >
                                    <ChevronRight size={32} />
                                </button>
                            </>
                        )}
                    </div>
                </div>
            )}

            <style>{`
                @keyframes spin {
                    0% { transform: rotate(0deg); }
                    100% { transform: rotate(360deg); }
                }
            `}</style>
        </div>
    );
};

const styles = {
    container: {
        maxWidth: '960px',
        margin: '0 auto',
        padding: '16px',
        backgroundColor: '#f8fafc',
        minHeight: '100vh',
    },
    breadcrumb: {
        display: 'flex',
        alignItems: 'center',
        gap: '8px',
        marginBottom: '12px',
    },
    backButton: {
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        width: '32px',
        height: '32px',
        backgroundColor: 'white',
        border: '1px solid #e2e8f0',
        borderRadius: '6px',
        cursor: 'pointer',
        color: '#64748b',
    },
    breadcrumbText: {
        fontSize: '12px',
        color: '#64748b',
    },
    productContainer: {
        display: 'grid',
        gridTemplateColumns: '1fr 1fr',
        gap: '24px',
        marginBottom: '24px',
        backgroundColor: 'white',
        borderRadius: '12px',
        padding: '20px',
        boxShadow: '0 1px 3px rgba(0,0,0,0.1)',
    },
    imageSection: {
        display: 'flex',
        flexDirection: 'column',
        gap: '10px',
    },
    mainImageContainer: {
        position: 'relative',
        width: '100%',
        aspectRatio: '1',
        backgroundColor: '#f8fafc',
        borderRadius: '10px',
        overflow: 'hidden',
        border: '1px solid #e2e8f0',
    },
    mainImage: {
        width: '100%',
        height: '100%',
        objectFit: 'contain',
        cursor: 'zoom-in',
    },
    discountBadge: {
        position: 'absolute',
        top: '10px',
        left: '10px',
        padding: '4px 8px',
        fontSize: '13px',
        fontWeight: '700',
        color: 'white',
        backgroundColor: '#ef4444',
        borderRadius: '6px',
    },
    noImage: {
        width: '100%',
        height: '100%',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
    },
    imageNav: {
        position: 'absolute',
        top: '50%',
        transform: 'translateY(-50%)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        width: '36px',
        height: '36px',
        backgroundColor: 'rgba(255, 255, 255, 0.95)',
        border: 'none',
        borderRadius: '50%',
        cursor: 'pointer',
        color: '#1e293b',
        boxShadow: '0 2px 6px rgba(0,0,0,0.12)',
    },
    imageCounter: {
        position: 'absolute',
        bottom: '16px',
        right: '16px',
        padding: '6px 12px',
        fontSize: '13px',
        fontWeight: '600',
        color: 'white',
        backgroundColor: 'rgba(0, 0, 0, 0.6)',
        borderRadius: '6px',
    },
    thumbnailGrid: {
        display: 'grid',
        gridTemplateColumns: 'repeat(auto-fill, minmax(52px, 1fr))',
        gap: '6px',
    },
    thumbnail: {
        aspectRatio: '1',
        borderRadius: '6px',
        overflow: 'hidden',
        cursor: 'pointer',
        transition: 'all 0.2s',
    },
    thumbnailImage: {
        width: '100%',
        height: '100%',
        objectFit: 'cover',
    },
    infoSection: {
        display: 'flex',
        flexDirection: 'column',
        gap: '10px',
    },
    topBadges: {
        display: 'flex',
        gap: '6px',
        flexWrap: 'wrap',
    },
    categoryBadge: {
        display: 'inline-block',
        padding: '3px 8px',
        fontSize: '11px',
        fontWeight: '600',
        color: '#6366f1',
        backgroundColor: '#eef2ff',
        borderRadius: '4px',
    },
    brandBadge: {
        display: 'inline-block',
        padding: '3px 8px',
        fontSize: '11px',
        fontWeight: '600',
        color: '#059669',
        backgroundColor: '#d1fae5',
        borderRadius: '4px',
    },
    productTitle: {
        fontSize: '20px',
        fontWeight: '700',
        color: '#1e293b',
        margin: 0,
        lineHeight: 1.3,
    },
    statsRow: {
        display: 'flex',
        gap: '12px',
        alignItems: 'center',
        paddingBottom: '10px',
        borderBottom: '1px solid #e2e8f0',
    },
    stat: {
        display: 'flex',
        alignItems: 'center',
        gap: '3px',
        fontSize: '12px',
        color: '#64748b',
    },
    priceSection: {
        padding: '8px 0',
    },
    originalPriceRow: {
        display: 'flex',
        alignItems: 'center',
        gap: '8px',
        marginBottom: '8px',
    },
    originalPriceLabel: {
        fontSize: '13px',
        color: '#94a3b8',
    },
    originalPrice: {
        fontSize: '15px',
        color: '#94a3b8',
        textDecoration: 'line-through',
    },
    salePriceRow: {
        display: 'flex',
        alignItems: 'baseline',
        gap: '8px',
    },
    discountRate: {
        fontSize: '22px',
        fontWeight: '700',
        color: '#ef4444',
    },
    salePrice: {
        fontSize: '24px',
        fontWeight: '700',
        color: '#1e293b',
    },
    unit: {
        fontSize: '13px',
        color: '#64748b',
    },
    deliveryInfo: {
        display: 'flex',
        flexDirection: 'column',
        gap: '6px',
        padding: '10px 12px',
        backgroundColor: '#f8fafc',
        borderRadius: '8px',
    },
    deliveryItem: {
        display: 'flex',
        alignItems: 'center',
        gap: '8px',
        fontSize: '12px',
        color: '#475569',
    },
    optionsSection: {
        padding: '14px',
        backgroundColor: '#f8fafc',
        borderRadius: '10px',
        border: '1px solid #e2e8f0',
    },
    optionsTitle: {
        display: 'flex',
        alignItems: 'center',
        gap: '6px',
        fontSize: '14px',
        fontWeight: '600',
        color: '#1e293b',
        marginBottom: '16px',
    },
    optionGroup: {
        marginBottom: '16px',
    },
    optionLabel: {
        display: 'block',
        fontSize: '13px',
        fontWeight: '600',
        color: '#475569',
        marginBottom: '8px',
    },
    requiredMark: {
        color: '#ef4444',
        marginLeft: '2px',
    },
    optionValues: {
        display: 'flex',
        flexWrap: 'wrap',
        gap: '8px',
    },
    optionButton: {
        display: 'flex',
        alignItems: 'center',
        gap: '5px',
        padding: '7px 12px',
        fontSize: '12px',
        fontWeight: '500',
        color: '#475569',
        backgroundColor: 'white',
        border: '1px solid #e2e8f0',
        borderRadius: '6px',
        cursor: 'pointer',
        transition: 'all 0.2s',
    },
    optionButtonSelected: {
        color: '#6366f1',
        backgroundColor: '#eef2ff',
        borderColor: '#6366f1',
        fontWeight: '600',
    },
    optionAddPrice: {
        fontSize: '11px',
        color: '#6366f1',
        fontWeight: '600',
    },
    checkIcon: {
        color: '#6366f1',
    },
    optionError: {
        display: 'flex',
        alignItems: 'center',
        gap: '6px',
        marginTop: '12px',
        padding: '10px 14px',
        fontSize: '13px',
        color: '#dc2626',
        backgroundColor: '#fef2f2',
        borderRadius: '8px',
    },
    quantitySection: {
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
    },
    quantityLeft: {
        display: 'flex',
        alignItems: 'center',
        gap: '8px',
    },
    quantityLabel: {
        fontSize: '14px',
        fontWeight: '600',
        color: '#1e293b',
    },
    minOrderBadge: {
        fontSize: '11px',
        fontWeight: '600',
        color: '#f59e0b',
        backgroundColor: '#fef3c7',
        padding: '4px 8px',
        borderRadius: '4px',
    },
    quantityControl: {
        display: 'flex',
        alignItems: 'center',
        gap: '8px',
    },
    quantityButton: {
        width: '30px',
        height: '30px',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        fontSize: '16px',
        fontWeight: '600',
        color: '#6366f1',
        backgroundColor: '#eef2ff',
        border: 'none',
        borderRadius: '6px',
        cursor: 'pointer',
    },
    quantityInput: {
        width: '56px',
        padding: '6px',
        fontSize: '14px',
        fontWeight: '600',
        textAlign: 'center',
        border: '1px solid #e2e8f0',
        borderRadius: '6px',
        outline: 'none',
    },
    stockInfo: {
        display: 'flex',
        alignItems: 'center',
        gap: '6px',
        padding: '8px 12px',
        backgroundColor: '#f8fafc',
        borderRadius: '6px',
    },
    stockLabel: {
        fontSize: '13px',
        fontWeight: '500',
        color: '#64748b',
    },
    stockValue: {
        fontSize: '13px',
        fontWeight: '700',
    },
    selectedProductBox: {
        padding: '16px',
        backgroundColor: '#f1f5f9',
        borderRadius: '10px',
        border: '1px solid #e2e8f0',
    },
    selectedProductInfo: {
        display: 'flex',
        flexDirection: 'column',
        gap: '4px',
        marginBottom: '8px',
    },
    selectedProductName: {
        fontSize: '14px',
        fontWeight: '600',
        color: '#1e293b',
    },
    selectedOptionsText: {
        fontSize: '12px',
        color: '#64748b',
    },
    selectedProductPrice: {
        fontSize: '14px',
        fontWeight: '600',
        color: '#6366f1',
        textAlign: 'right',
    },
    totalSection: {
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        padding: '14px 16px',
        backgroundColor: '#1e293b',
        borderRadius: '10px',
    },
    totalLabel: {
        fontSize: '14px',
        fontWeight: '600',
        color: 'white',
    },
    totalRight: {
        display: 'flex',
        alignItems: 'baseline',
        gap: '8px',
    },
    totalQuantity: {
        fontSize: '14px',
        color: '#94a3b8',
    },
    totalPrice: {
        fontSize: '22px',
        fontWeight: '700',
        color: 'white',
    },
    actionButtons: {
        display: 'flex',
        gap: '8px',
    },
    likeButton: {
        width: '44px',
        height: '44px',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        backgroundColor: 'white',
        border: '1.5px solid #e2e8f0',
        borderRadius: '10px',
        cursor: 'pointer',
        transition: 'all 0.2s',
    },
    cartButton: {
        flex: 1,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        gap: '6px',
        padding: '12px',
        fontSize: '14px',
        fontWeight: '600',
        color: '#6366f1',
        backgroundColor: '#eef2ff',
        border: '1.5px solid #6366f1',
        borderRadius: '10px',
        cursor: 'pointer',
        transition: 'all 0.2s',
    },
    buyButton: {
        flex: 1,
        padding: '12px',
        fontSize: '14px',
        fontWeight: '600',
        color: 'white',
        background: 'linear-gradient(135deg, #6366f1 0%, #8b5cf6 100%)',
        border: 'none',
        borderRadius: '10px',
        cursor: 'pointer',
        transition: 'all 0.2s',
    },
    inquiryButton: {
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        gap: '6px',
        width: '100%',
        padding: '10px',
        fontSize: '13px',
        fontWeight: '600',
        color: '#475569',
        backgroundColor: '#f8fafc',
        border: '1.5px solid #e2e8f0',
        borderRadius: '10px',
        cursor: 'pointer',
        transition: 'all 0.2s',
    },
    sellerInfo: {
        display: 'flex',
        alignItems: 'center',
        gap: '10px',
        padding: '10px 12px',
        backgroundColor: '#f8fafc',
        borderRadius: '8px',
    },
    sellerDetails: {
        flex: 1,
    },
    sellerLabel: {
        fontSize: '11px',
        color: '#94a3b8',
    },
    sellerName: {
        fontSize: '14px',
        fontWeight: '600',
        color: '#1e293b',
    },
    sellerSubName: {
        fontSize: '12px',
        color: '#64748b',
        marginTop: '2px',
    },
    sellerButton: {
        padding: '8px 16px',
        fontSize: '12px',
        fontWeight: '600',
        color: '#6366f1',
        backgroundColor: 'white',
        border: '1px solid #6366f1',
        borderRadius: '6px',
        cursor: 'pointer',
    },
    detailSection: {
        backgroundColor: 'white',
        borderRadius: '12px',
        overflow: 'hidden',
        boxShadow: '0 1px 3px rgba(0,0,0,0.1)',
    },
    detailTabs: {
        display: 'flex',
        borderBottom: '1px solid #e2e8f0',
    },
    tab: {
        flex: 1,
        padding: '12px 16px',
        fontSize: '13px',
        fontWeight: '600',
        color: '#94a3b8',
        backgroundColor: 'transparent',
        border: 'none',
        cursor: 'pointer',
        transition: 'all 0.2s',
    },
    activeTab: {
        color: '#6366f1',
        backgroundColor: '#f8fafc',
        borderBottom: '2.5px solid #6366f1',
    },
    detailContent: {
        padding: '20px',
    },
    descriptionSection: {
        marginBottom: '20px',
    },
    detailTitle: {
        fontSize: '16px',
        fontWeight: '700',
        color: '#1e293b',
        marginBottom: '12px',
    },
    descriptionText: {
        fontSize: '13px',
        lineHeight: 1.7,
        color: '#475569',
        whiteSpace: 'pre-wrap',
    },
    specsTable: {
        overflowX: 'auto',
    },
    infoTable: {
        width: '100%',
        borderCollapse: 'collapse',
    },
    infoTh: {
        width: '140px',
        padding: '14px 16px',
        fontSize: '13px',
        fontWeight: '600',
        color: '#64748b',
        backgroundColor: '#f8fafc',
        textAlign: 'left',
        borderBottom: '1px solid #e2e8f0',
    },
    infoTd: {
        padding: '14px 16px',
        fontSize: '14px',
        color: '#1e293b',
        borderBottom: '1px solid #e2e8f0',
    },
    shippingInfo: {
        display: 'flex',
        flexDirection: 'column',
        gap: '20px',
    },
    shippingCard: {
        padding: '24px',
        backgroundColor: '#f8fafc',
        borderRadius: '12px',
    },
    shippingTitle: {
        display: 'flex',
        alignItems: 'center',
        gap: '8px',
        fontSize: '16px',
        fontWeight: '700',
        color: '#1e293b',
        marginBottom: '12px',
    },
    shippingText: {
        fontSize: '14px',
        lineHeight: 1.7,
        color: '#475569',
        whiteSpace: 'pre-wrap',
        margin: 0,
    },
    loadingContainer: {
        display: 'flex',
        flexDirection: 'column',
        justifyContent: 'center',
        alignItems: 'center',
        height: '400px',
        gap: '16px',
    },
    spinner: {
        width: '40px',
        height: '40px',
        border: '3px solid #e2e8f0',
        borderTop: '3px solid #6366f1',
        borderRadius: '50%',
        animation: 'spin 1s linear infinite',
    },
    loadingText: {
        fontSize: '14px',
        color: '#64748b',
    },
    modalOverlay: {
        position: 'fixed',
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        backgroundColor: 'rgba(0, 0, 0, 0.9)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        zIndex: 1000,
    },
    imageModal: {
        position: 'relative',
        maxWidth: '90vw',
        maxHeight: '90vh',
    },
    modalImage: {
        maxWidth: '100%',
        maxHeight: '90vh',
        objectFit: 'contain',
    },
    modalClose: {
        position: 'absolute',
        top: '-60px',
        right: '0',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        width: '48px',
        height: '48px',
        backgroundColor: 'white',
        border: 'none',
        borderRadius: '50%',
        cursor: 'pointer',
        color: '#1e293b',
    },
    modalNav: {
        position: 'absolute',
        top: '50%',
        transform: 'translateY(-50%)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        width: '56px',
        height: '56px',
        backgroundColor: 'rgba(255, 255, 255, 0.9)',
        border: 'none',
        borderRadius: '50%',
        cursor: 'pointer',
        color: '#1e293b',
    },
};

export default ProductDetail;
